import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "app/lib/api";

import Button from "@mui/material/Button";
import {
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Divider,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ToggleButtons from "components/common/toggle_button/ToggleButton";
import StatusLabel from "components/common/status_label/StatusLabel";

import { dateFilter, textFilter } from "utils/table";
import CustomTable from "components/common/table/CustomTable";
import { hl7JobsApi, useGetHl7JobsQuery } from "app/services/job/hl7Jobs";
import { useDispatch } from "react-redux";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "static/pagination";
import {
  formatDateTime,
  isValidDateFormat,
  formatCustomDateTime,
} from "utils/date";
import { DATE_FORMAT } from "static/date";
import {
  ReplayOutlined,
  Add,
  Edit,
  Apartment,
  PhoneForwardedOutlined,
} from "@mui/icons-material";
import { showErrorToast, showSuccessToast } from "utils/toast";
import useDebounce from "app/hooks/useDebounce";
import {
  CLINIC_NAMES,
  HL7_RESULT_STATUS,
  HL7_EXPORT_STATUS,
} from "static/constants";

const useHl7JobsTable = (alertType, setAlertType) => {
  const [open, setOpen] = useState(false);
  const [dataRow, setDataRow] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [operation, setOperation] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hl7JobsData, setHl7JobsData] = useState([]);
  const [updateStatus, setUpdateStatus] = useState("");
  const [rowPageCount, setRowPageCount] = useState(20);
  const [showEditCsn, setShowEditCsn] = useState(null);
  const [editingCsnId, setEditingCsnId] = useState(null);
  const [updateMessage, setUpdateMessage] = useState("");
  const [showAdtModal, setShowAdtModal] = useState(false);
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [selectedRowsCount, setSelectedRowsCount] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAdtSidebar, setShowAdtSidebar] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        setShowAdtSidebar(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const [sorting, setSorting] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const debouncedSetSearch = useDebounce((value) => {
    setDebouncedSearch(value);
  }, 500);

  useEffect(() => {
    debouncedSetSearch(search);
  }, [search, debouncedSetSearch]);

  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const alertTypeFilters = useMemo(() => {
    const filters = columnFilters.filter(
      (filter) => filter.id !== "alert_flag" && filter.id !== "warning_flag"
    );
    if (alertType === "Critical") {
      filters.push({ id: "alert_flag", value: true });
    } else if (alertType === "Warning") {
      filters.push({ id: "warning_flag", value: true });
      filters.push({ id: "alert_flag", value: false });
    }
    return filters;
  }, [columnFilters, alertType]);

  const queryParams = useMemo(
    () => ({
      search: debouncedSearch,
      sorting,
      filters: alertTypeFilters,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
    }),
    [
      debouncedSearch,
      sorting,
      alertTypeFilters,
      pagination.pageIndex,
      pagination.pageSize,
    ]
  );

  const {
    data: hl7JobsResult,
    isLoading: isLoadingHl7Jobs,
    isFetching: isFetchingHl7Jobs,
  } = useGetHl7JobsQuery(queryParams, {
    skip: debouncedSearch.length > 0 && debouncedSearch.length < 3,
  });

  const dispatch = useDispatch();

  const handleAlertTypeChange = (event, newAlertType) => {
    if (newAlertType !== null) {
      setAlertType(newAlertType);
    }
  };

  const shouldDisableRetry = (rowData) => {
    return (
      rowData?.account === CLINIC_NAMES.CENTRA &&
      rowData?.result_status === HL7_RESULT_STATUS.Preliminary &&
      rowData?.status === HL7_EXPORT_STATUS.Completed
    );
  };

  const alertTypeButtons = [
    {
      value: "All",
      label: "All",
      color: "success",
      style: { textTransform: "none" },
    },
    {
      value: "Critical",
      label: "Critical",
      color: "success",
      style: { textTransform: "none" },
    },
    {
      value: "Warning",
      label: "Warning",
      color: "success",
      style: { textTransform: "none" },
    },
  ];

  const transmissionTypeIconMap = {
    inOffice: <Apartment className="icon" color="info" fontSize="small" />,
    connectivityStatus: (
      <PhoneForwardedOutlined className="icon" color="info" fontSize="small" />
    ),
  };

  const columns = useMemo(
    () => [
      {
        header: "Report ID",
        accessorKey: "report_id",
        filterVariant: "text",
        filterFn: textFilter,
        enableSorting: false,
        size: 180,
        Cell: ({ row }) => {
          const rowData = row?.original;
          return (
            <Link
              to={`/patient/report/${rowData?.report_id}`}
              target="_blank"
              style={{ textDecoration: "none" }}
            >
              {rowData.report_id}
            </Link>
          );
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        filterVariant: "text",
        size: 120,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const name =
            `${rowData?.firstName || ""} ${rowData?.lastName || ""}`.trim();
          const transmissionTypeIcon =
            transmissionTypeIconMap[rowData?.transmission_type];

          return (
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="body2">{name || "—"}</Typography>
              </Grid>
              {transmissionTypeIcon && <Grid item>{transmissionTypeIcon}</Grid>}
            </Grid>
          );
        },
      },
      {
        header: "HL7 Type",
        accessorKey: "type",
        filterVariant: "select",
        enableSorting: false,
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "ORU", value: "ORU_R01" },
          { label: "DFT", value: "DFT_P03" },
        ],
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        header: "CSN/Order ID",
        accessorKey: "csn",
        filterVariant: "text",
        enableSorting: false,
        size: 120,
        filterFn: textFilter,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const isDisabled = shouldDisableRetry(rowData);
          if (!rowData?.csn?.trim() && !rowData?.order_number?.trim()) {
            return (
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isDisabled) handleCsnModal(rowData, e);
                }}
                style={{
                  textDecoration: "none",
                  color: isDisabled ? "#ccc" : "#94BA33",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  pointerEvents: isDisabled ? "none" : "auto",
                }}
              >
                <Add fontSize="small" />
                Add CSN / OrderID
              </Link>
            );
          } else {
            return (
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleCsnModal(rowData, e);
                }}
                style={{
                  textDecoration: "none",
                  color: editingCsnId === rowData._id ? "#555" : "#000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>
                  {rowData?.csn?.trim() || rowData?.order_number?.trim()}
                </span>
                <Edit
                  fontSize="small"
                  style={{ color: "#94BA33", marginRight: "8px" }}
                />
              </Link>
            );
          }
        },
      },
      {
        header: "Account",
        accessorKey: "account",
        filterVariant: "text",
        enableSorting: false,
        filterFn: textFilter,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        header: "MRN",
        accessorKey: "mrn",
        filterVariant: "text",
        enableSorting: false,
        size: 90,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        header: "DOB",
        accessorKey: "dob",
        filterVariant: "date",
        filterFn: dateFilter,
        enableSorting: false,
        size: 120,
        Cell: ({ cell }) => {
          const dob = cell.getValue();
          const formattedDob = formatDateTime(dob, DATE_FORMAT.DEFAULT);
          return isValidDateFormat(formattedDob, DATE_FORMAT.DEFAULT)
            ? formattedDob
            : "—";
        },
      },
      {
        header: "Report Date",
        accessorKey: "report_date",
        filterVariant: "date",
        filterFn: dateFilter,
        size: 120,
        Cell: ({ cell }) => {
          const reportDate = cell.getValue();
          const formattedReportDate = formatDateTime(
            reportDate,
            DATE_FORMAT.DEFAULT
          );
          return isValidDateFormat(formattedReportDate, DATE_FORMAT.DEFAULT)
            ? formattedReportDate
            : "—";
        },
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        filterVariant: "date",
        filterFn: dateFilter,
        size: 120,
        Cell: ({ cell }) => {
          const createdAt = cell.getValue();
          const formatted = formatCustomDateTime(createdAt);
          return (
            <div
              style={{
                whiteSpace: "pre-line",
                fontSize: "12px",
                lineHeight: "1.2",
              }}
            >
              {formatted}
            </div>
          );
        },
      },
      {
        header: "Updated At",
        accessorKey: "updated_at",
        filterVariant: "date",
        filterFn: dateFilter,
        size: 120,
        Cell: ({ cell }) => {
          const updatedAt = cell.getValue();
          const formatted = formatCustomDateTime(updatedAt);
          return (
            <div
              style={{
                whiteSpace: "pre-line",
                fontSize: "12px",
                lineHeight: "1.2",
              }}
            >
              {formatted}
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        filterVariant: "select",
        enableColumnFilter: true,
        enableSorting: false,
        size: 120,
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "Completed", value: "completed" },
          { label: "Pending", value: "pending" },
          { label: "Failed", value: "error" },
          { label: "Timeout", value: "timeout" },
        ],
        accessorFn: (rowData) => {
          return (
            <StatusLabel
              status={rowData?.status}
              failedReason={rowData?.failed_reason}
            />
          );
        },
      },
      {
        header: "Actions",
        accessorKey: "options",
        enableColumnFilter: false,
        size: 100,
        enableSorting: false,
        accessorFn: (rowData) => {
          const shouldDisableRetryButton = shouldDisableRetry(rowData);
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleConfirmModal(rowData, "retry")}
                startIcon={<ReplayOutlined />}
                disabled={shouldDisableRetryButton}
                style={{
                  borderColor: shouldDisableRetryButton ? "#ccc" : "#94BA33",
                  color: shouldDisableRetryButton ? "#ccc" : "#94BA33",
                  fontSize: "11px",
                  minWidth: "auto",
                }}
              >
                Resend
              </Button>
            </div>
          );
        },
      },
    ],
    [editingCsnId]
  );

  const filteredData = hl7JobsResult?.jobs || [];

  const hl7JobsTableMemo = useMemo(
    () => (
      <CustomTable
        data={filteredData}
        columns={columns}
        options={{
          enableSorting: true,
          enableTopToolbar: false,
          rowCount: hl7JobsResult?.totalCount || 0,
          state: {
            sorting,
            pagination,
            showGlobalFilter: false,
            showColumnFilters: true,
            isLoading: isLoadingHl7Jobs || isFetchingHl7Jobs,
          },
          defaultColumn: {
            size: 100,
          },
          rowsPerPageOptions: DEFAULT_PAGE_SIZE_OPTIONS,
          enableRowSelection: (row) => !shouldDisableRetry(row.original),
          onSelectionChange: (e) => handleSelection(e),
          manualPagination: true,
          manualFiltering: true,
          onPaginationChange: setPagination,
          onColumnFiltersChange: setColumnFilters,
          onSortingChange: setSorting,
          muiTableBodyRowProps: ({ row }) => {
            const bgColor = row.original.alert_flag
              ? "#F9D0D0"
              : row.original.warning_flag
                ? "#FFF4E5"
                : "inherit";
            return {
              sx: {
                backgroundColor: bgColor,
                "&.MuiTableRow-root.Mui-selected": {
                  backgroundColor: `${bgColor} !important`,
                },
                "&.MuiTableRow-root.Mui-selected td:after": {
                  background: "none !important",
                },
              },
            };
          },
        }}
      />
    ),
    [filteredData, isLoadingHl7Jobs, isFetchingHl7Jobs, sorting, pagination]
  );

  const tableWithToolbar = useMemo(
    () => (
      <div>
        <div
          style={{
            padding: "16px",
            border: "1px solid rgb(224, 224, 224)",
            borderBottom: "none",
            backgroundColor: "#ffffff",
            marginRight: "15px",
            position: "sticky",
            top: "0",
            zIndex: 100,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <TextField
                size="small"
                placeholder="Search by Report ID or Name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "300px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Typography variant="body2">Alert Type</Typography>
                <ToggleButtons
                  size="small"
                  value={alertType}
                  style={{ maxHeight: "35px" }}
                  sx={{
                    "& .Mui-selected": {
                      backgroundColor: "#F1F6E3",
                      color: "#638215",
                    },
                  }}
                  onChange={handleAlertTypeChange}
                  buttons={alertTypeButtons}
                />
                <Divider
                  orientation="vertical"
                  variant="middle"
                  style={{ height: "24px", margin: "0 10px" }}
                />
                <Tooltip title="Browse ADT (Ctrl/Cmd + K)">
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setShowAdtSidebar(true)}
                  >
                    Browse ADT
                  </Button>
                </Tooltip>
              </div>
            </Grid>
          </Grid>
        </div>
        {hl7JobsTableMemo}
      </div>
    ),
    [
      hl7JobsTableMemo,
      search,
      alertType,
      handleAlertTypeChange,
      alertTypeButtons,
    ]
  );

  const getHl7TableData = async () => {
    dispatch(
      hl7JobsApi.util.invalidateTags([
        "hl7Jobs",
        {
          search: debouncedSearch,
          sorting,
          filters: columnFilters,
          page: pagination.pageIndex,
          pageSize: pagination.pageSize,
        },
      ])
    );
  };

  const handleConfirmModal = (rowData, op) => {
    setDataRow(rowData);
    setOperation(op);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handleCsnModal = (rowData, event) => {
    setDataRow(rowData);
    setEditingCsnId(rowData._id);
    const rect = event.currentTarget.getBoundingClientRect();
    const anchorPosition = {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    };
    setShowEditCsn({ element: event.currentTarget, position: anchorPosition });
  };

  const closeCsnModal = () => {
    setShowEditCsn(null);
    setEditingCsnId(null);
  };

  const openAdtModal = (rowData) => {
    setDataRow(rowData);
    setShowAdtModal(true);
  };

  const closeAdtModal = () => {
    setShowAdtModal(false);
  };

  const handleSelection = (e) => {
    setSelectedRowsCount(e.length);
    setSelectedRowsData(e);
  };

  const handleRetry = async (rowData) => {
    setIsLoading(true);
    try {
      await api.put("/hl7job/v2/retry", rowData);
      showSuccessToast("Job retried successfully!");
      getHl7TableData();
    } catch (error) {
      showErrorToast(error.message || "Something went wrong!");
    }
    setIsLoading(false);
  };

  const handleUpdateJob = async (jobId, newData) => {
    setIsLoading(true);
    try {
      await api.put(`/hl7job/v2/update/${jobId}`, newData);
      showSuccessToast("CSN updated successfully!");
      closeCsnModal();
      getHl7TableData();
    } catch (error) {
      showErrorToast(error.message || "Something went wrong!");
    }
    setIsLoading(false);
  };

  return {
    open,
    dataRow,
    errorMsg,
    isLoading,
    operation,
    hl7JobsData,
    showEditCsn,
    rowPageCount,
    updateStatus,
    updateMessage,
    showAdtModal,
    showConfirmModal,
    selectedRowsData,
    selectedRowsCount,
    showAdtSidebar,

    setOpen,
    setDataRow,
    setErrorMsg,
    setIsLoading,
    setOperation,
    setShowEditCsn,
    setHl7JobsData,
    setUpdateStatus,
    setRowPageCount,
    setShowAdtModal,
    setUpdateMessage,
    setShowConfirmModal,
    setSelectedRowsData,
    setSelectedRowsCount,
    setShowAdtSidebar,

    openAdtModal,
    closeAdtModal,
    handleSelection,
    closeConfirmModal,
    closeCsnModal,
    handleConfirmModal,
    handleCsnModal,

    handleRetry,
    getHl7TableData,
    handleUpdateJob,

    setSorting,
    setPagination,
    setColumnFilters,

    hl7JobsTableMemo: tableWithToolbar,
  };
};

export default useHl7JobsTable;
